'use client';
import { AjustedVariant } from '@modules/products/templates';
import { BusinessInfo } from 'types/business';
import InputQuantity from './input-quantity';
import MediumGiftCardAmount from './giftcard-amount';
import MediumGiftCardInformation from './giftcard-information';
import ReviewCheckoutButtons from './giftcard-review-checkout-buttons';
import { P } from '@modules/common/components/typography/P';
import { useFormikContext } from 'formik';
import { isMobileDevices } from '@lib/util/is-mobile-devices';
import { useContext, useEffect, useState, useRef } from 'react';
import { CartDropdownContext } from '@lib/context/cart-dropdown-context';
import { currencyToDisplayPoints } from '@lib/util/parser';
import {
  CARD_PROGRAMS,
  CUSTOMER_SERVICE_PHONE_NUMBER,
  CUSTOMER_SERVICE_PHONE_NUMBER_TEL,
  DEFAULT_SEARCH_PROVIDER,
} from '@lib/constants';
import { Imagery } from '@modules/home/components/imagery/imagery';
import { formatAmount, useCart } from 'medusa-react';
import { domains } from '@lib/constants/domains';
import { useImageBySearch } from '@lib/hooks/use-image-by-search';
import { PersonTag } from '@modules/common/components/person-tag';
import letThemChooseUtil from '@lib/util/let-them-choose-util';
import { GiftCardBanner } from './giftcard-banner';
import { GiftCardMoreAbout } from './giftcard-moreAbout';
import { GiftCardLocal } from './giftcard-local-gift-cards';
import { GiftCardFAQs } from './giftcard-faqs';
import { GiftCardSimilarGifts } from './giftcard-similarGifts';
import { GeolocationTopResults } from './giftcard-geolocation-top-results';
import MediumGiftCardInformationInPhoto from './giftcard-information-in-photo';
import GoogleMapsLink from '@modules/business/components/google-maps-link';
import { useStoreClosed } from '@lib/context/store-closed-context';

type Props = {
  business: BusinessInfo;
  variants: AjustedVariant[];
  customVariants: AjustedVariant[];
  isOptedOut: boolean;
  isClickSubmit: boolean;
  setIsClickSubmit: (isClickSubmit: boolean) => void;
  setIsTextMethod: (isTextMethod: boolean) => void;
  summaries?: {
    short: string;
    long: string;
  };
};

const GiftCardTemplate = (props: Props) => {
  const [imageTitle, setImageTitle] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [hasScrolled, setHasScrolled] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const {
    business,
    variants,
    customVariants,
    summaries,
  } = props;
  const form = useFormikContext<any>();
  const giftCardType = form.getFieldProps('giftCardType');
  const amount = form.getFieldProps('variant');
  const cartContext = useContext(CartDropdownContext);
  const { cart } = useCart();
  const { imageSrc } = useImageBySearch(business.image_url, business.search_provider || DEFAULT_SEARCH_PROVIDER, 860);
  const storeClosed = useStoreClosed();

  // Separate useEffect for scrolling - runs only once on component mount
  useEffect(() => {
    // Scroll to image on mobile devices after component mounts, but only once
    if (typeof window !== 'undefined' && isMobileDevices() && imageContainerRef.current && !hasScrolled) {
      // Small delay to ensure the page is fully rendered
      setTimeout(() => {
        setHasScrolled(true);
      }, 100);
    }
  }, [hasScrolled]);

  // // Original useEffect for other functionality
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const cardProgram = sessionStorage.getItem(CARD_PROGRAMS.KEY);
      const { title, img_url } = letThemChooseUtil.getGiftCardImageAndTitle(
        cardProgram ?? '',
        storeClosed.availableCyoThemeCards
      );
      setImageTitle(title);
      setImageUrl(img_url);
    }
  }, [giftCardType]);

  return (
    <div className="mt-6">
      <div className="max-w-[1200px] px-4 md:py-6 mx-auto">
        <div className="product-details">
          <div ref={imageContainerRef} className="product-image relative w-full md:w-[360px] md:flex-none lg:w-[460px]">
            {business.id && (
              <div className="product-header md:hidden">
                <MediumGiftCardInformationInPhoto
                  location={business.location}
                  rating={business.rating}
                  review_count={business.review_count}
                  name={business.name}
                  phone={business.phone}
                  coordinates={business.coordinates}
                  distance={business.distance}
                  url={business.url}
                  id={business.id}
                  search_provider={business?.search_provider || DEFAULT_SEARCH_PROVIDER}
                />
              </div>
            )}
            <PersonTag place_id={business.id} author_display_name={business.author_display_name} />
            {business?.id ? (
              <div
                className={`w-full aspect-[3/2] md:aspect-[4/3] relative ${
                  imageSrc ? 'bg-cover' : 'bg-contain'
                } bg-no-repeat bg-center overflow-hidden rounded-t`}
                style={{ backgroundImage: `url(${imageSrc || '/main-logo.png'})` }}
              >
                <img
                  src={imageSrc || '/logo-gcm.png'}
                  alt={business.name}
                  className="w-full max-w-ful absolute top-0 opacity-0"
                  style={{ objectFit: 'contain' }}
                />
              </div>
            ) : (
              <div className="rounded border border-black shadow-input w-full p-4">
                <Imagery url={imageUrl} title={imageTitle} styleText="bg-pink_drink" isChooseYourOwn={true} />
              </div>
            )}
            {business.id && (
              <div className="product-description md:hidden">
                {summaries?.short && (
                  <div className="product-description-content">
                    <div className="product-description-line"></div>
                    <div>
                      <p>{summaries?.short}</p>
                    </div>
                  </div>
                )}
                <div className="product-description-address">
                  <div className="product-description-addressItem product-description-addressItem-maplink">
                    <img src="/map-icon.svg" alt="Location Icon" className="inline mr-2" />
                    <GoogleMapsLink
                      id={business?.id}
                      search_provider={business?.search_provider ?? DEFAULT_SEARCH_PROVIDER}
                      coordinates={business?.coordinates}
                      mapUrl={business?.url}
                      address={business?.location?.display_address?.map((item: string) => item).join(', ')}
                    />
                  </div>
                  {business?.phone && (
                    <div className="product-description-addressItem flex-none">
                      <img src="/phone-icon.svg" alt="Location Icon" className="inline mr-2" />
                      <div>
                        <a href={`tel:${business.phone}`}>{business.phone}</a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="w-full ml-4">
            <MediumGiftCardInformation
              location={business.location}
              rating={business.rating}
              review_count={business.review_count}
              name={business.name}
              phone={business.phone}
              coordinates={business.coordinates}
              distance={business.distance}
              url={business.url}
              id={business.id}
              search_provider={business?.search_provider ?? DEFAULT_SEARCH_PROVIDER}
              description={summaries?.short ?? ''}
            />
            <div className="mt-2">
              <div className="">
                <MediumGiftCardAmount variants={variants} customVariants={customVariants} />
              </div>
              {cartContext?.pointMode ? (
                <P className="text-gray-ada mt-2 text-base font-normal">
                  <div className="flex items-center gap-2">
                    <div className="text-right text-base text-black font-medium">Total:</div>
                    <div className="flex flex-wrap">
                      {currencyToDisplayPoints(
                        amount?.value?.prices || (variants?.length > 0 ? variants[0].prices : 0)
                      )}
                    </div>
                  </div>
                </P>
              ) : (
                <P className="!text-[#808080] mt-3 text-sm font-normal italic">
                  A processing fee of{' '}
                  <strong>
                    {formatAmount({
                      amount: amount?.value?.fee || 0,
                      region: cart?.region || { currency_code: 'USD', tax_rate: 0, tax_code: 'USD' },
                      includeTaxes: false,
                    })}{' '}
                    per card
                  </strong>{' '}
                  will be applied at checkout to help cover secure digital delivery and payment handling
                </P>
              )}
              <div className="card__quantity hidden">
                <InputQuantity />
              </div>
            </div>
          </div>
        </div>

        <div className="w-full ml-4 mt-4">
          <div className="flex w-full gap-2 justify-center pt-4">
            <ReviewCheckoutButtons businessName={business.name} />
          </div>
        </div>
      </div>
      <div className="product-banner">
        <div className="container-page static-content">
          <GiftCardBanner />
        </div>
      </div>
      {summaries?.long && (
        <div className="container-page static-content max-w-[1034px]">
          <GiftCardMoreAbout desc={summaries.long} title={business.name} />
        </div>
      )}
      {business?.id && (
        <div className="container-page container-page-wider static-content">
          <GiftCardSimilarGifts business={business} />
        </div>
      )}
      {business?.id && (
        <div className="container-page container-page-wider static-content">
          <GeolocationTopResults business={business} />
        </div>
      )}
      <div className="container-page static-content max-w-[1034px]">
        <GiftCardLocal />
      </div>
      <div className="product-faqs">
        <div className="container-page static-content max-w-[1034px]">
          <img src="/questionIcon.svg" alt="FAQ Icon" className="block mx-auto  -mt-9" />
          <h2 className="mt-3">FAQs</h2>
          <GiftCardFAQs />
        </div>
      </div>
      <div className="container-page static-content mt-[35px] md:pt-10 container-page-center">
        <P>
          Give Local at The Gift Card Market – the most convenient way to send Local Prepaid Gift Cards in just minutes.
          You can send Local Prepaid Gift Cards via text, email, or print at home at{' '}
          <a href={domains.prod}>{domains.prod}.</a> Local Prepaid Gift Cards are ready for use immediately once
          redeemed. Funds never expire and there are no post purchase fees.
        </P>
        <P className="mt-5 break-words">
          Funds can be used at the selected merchant at locations in the United States. If you would like to switch the
          merchant of choice, you can do so on the Redemption page at no charge. Local Prepaid Gift Cards can be used in
          person or over the phone. <a href="https://app.akimbocard.com/app/#/akimbo/cardBalance">Click Here</a> to
          Check Balance on your Prepaid Gift Card.
        </P>
        <P className="mt-5">
          If you have any questions or need more information, please contact our support team at{' '}
          <a href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER_TEL}`} className="text-pink_drink">
            {CUSTOMER_SERVICE_PHONE_NUMBER}
          </a>
          , visit us at <a href="/contact-us">{domains.prod}/contact-us</a> or reference our <a href="/faqs">FAQ’s</a>.
        </P>
      </div>
    </div>
  );
};

export default GiftCardTemplate;
